import { Link, useNavigate } from 'react-router-dom'
import { Inbox, Mail, MailOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/hooks'
import { useRecentInbox, useMarkInboxRead } from '@/hooks/useInbox'
import type { InboxMessage } from '@/types'

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'сега'
  if (diffMin < 60) return `${diffMin} мин`
  if (diffHours < 24) return `${diffHours} ч`
  if (diffDays < 7) return `${diffDays} д`
  return date.toLocaleDateString('bg-BG', { day: 'numeric', month: 'short' })
}

export interface InboxDropdownProps {
  onClose: () => void
  fullHeight?: boolean
}

export function InboxDropdown({ onClose, fullHeight }: InboxDropdownProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { messages, isLoading } = useRecentInbox(5)
  const { markAsRead } = useMarkInboxRead()

  const handleItemClick = (message: InboxMessage) => {
    if (!message.is_read) {
      markAsRead(message.id)
    }
    navigate(`/inbox?id=${message.id}`)
    onClose()
  }

  return (
    <div className={cn(
      'w-full overflow-hidden',
      fullHeight ? 'bg-white' : 'min-w-[320px] max-w-[380px] bg-white rounded-lg shadow-lg border border-gray-100'
    )}>
      {/* Header — hidden when embedded in BottomSheet (title provided by sheet) */}
      {!fullHeight && (
        <div className="px-4 py-3 border-b border-gray-100">
          <span className="text-[15px] font-semibold text-gray-900">
            {t('inbox.title')}
          </span>
        </div>
      )}

      {/* Items list */}
      <div className={cn(fullHeight ? '' : 'max-h-[340px]', 'overflow-y-auto')}>
        {isLoading ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">
            {t('common.loading')}
          </div>
        ) : messages.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">
            {t('inbox.empty.title')}
          </div>
        ) : (
          messages.map((message, index) => {
            const isUnread = !message.is_read
            return (
              <button
                key={message.id}
                type="button"
                onClick={() => handleItemClick(message)}
                className={cn(
                  'w-full text-left px-4 py-3 flex items-start gap-3 transition-colors',
                  isUnread ? 'bg-blue-50 hover:bg-blue-100/70' : 'bg-white hover:bg-gray-50',
                  index > 0 && 'border-t border-gray-100'
                )}
              >
                {/* Read status icon */}
                <div className="flex-shrink-0 mt-0.5">
                  {isUnread ? (
                    <Mail className="w-4 h-4 text-accent" />
                  ) : (
                    <MailOpen className="w-4 h-4 text-gray-300" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      'text-[14px] truncate',
                      isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-600'
                    )}
                  >
                    {message.title || 'Без заглавие'}
                  </p>
                  {(message.metadata as Record<string, unknown> | null)?.publisher ? (
                    <p className="text-[13px] text-gray-400 truncate mt-0.5">
                      {(message.metadata as Record<string, unknown>).publisher as string}
                      {typeof (message.metadata as Record<string, unknown>).update_type_name === 'string' && (
                        <span> &middot; {String((message.metadata as Record<string, unknown>).update_type_name)}</span>
                      )}
                    </p>
                  ) : message.body_preview ? (
                    <p className="text-[13px] text-gray-400 truncate mt-0.5">
                      {message.body_preview}
                    </p>
                  ) : null}
                  <p className="text-[11px] text-gray-400 mt-1">
                    {formatRelativeTime(message.created_at)}
                  </p>
                </div>
              </button>
            )
          })
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100">
        <Link
          to="/inbox"
          onClick={onClose}
          className="group flex w-full items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-primary-light transition-colors"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500 transition-colors group-hover:text-primary-light">
            <Inbox className="w-4 h-4" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-[14px] font-medium">{t('inbox.dropdown.seeAll')}</span>
            <span className="block truncate text-[12px] text-gray-500 transition-colors group-hover:text-primary-light">
              {t('inbox.dropdown.seeAllHint')}
            </span>
          </span>
        </Link>
      </div>
    </div>
  )
}
